import { ChangeEvent, Fragment, memo, useCallback, useEffect, useMemo, useRef } from 'react';

import Divider from '@material-ui/core/Divider';
import MenuItem from '@material-ui/core/MenuItem';

import { useAlert } from '../../hooks';
import { getFlooredFixed, isFileAccepted } from '../../utils';
import { Select } from '../select/Select';
import { TextArea } from '../textArea/TextArea';
import { AttachmentsSection } from './attachmentsSection/AttachmentsSection';
import { BrowseFileButton } from './browseFileButton/BrowseFileButton';
import { DropZone } from './dropZone/DropZone';
import { NewAttachmentCard } from './newAttachmentCard/NewAttachmentCard';
import { useStyles } from './uploadAttachment.styles';
import { Attachment, UploadAttachmentProps } from './uploadAttachment.types';

export const UploadAttachment = memo(
  ({
    variant = 'default',
    selectedFile,
    description,
    selectedType,
    attachments,
    newAttachments,
    attachmentTypes,
    amountLimit,
    acceptFormats,
    required,
    hasError,
    hideLabel,
    showTypesOfAttachment = false,
    multiple,
    enableDragAndDrop,
    hideBottomAttachmentsSection,
    fileSizeValidation,
    loading,
    disabled,
    changeSelectedFile,
    changeSelectedType,
    changeDescription,
    onAddFiles,
    onPreviewAttachment,
    onDeleteAttachment,
    onChangeAttachmentType,
    onChangeAttachmentDescription,
  }: UploadAttachmentProps) => {
    const styles = useStyles();

    const { showAlert } = useAlert();

    const inputFileRef = useRef<HTMLInputElement>(null);

    const isMulti = useMemo(() => !!attachments, [attachments]);

    const showTypeSelect = useMemo(
      () => showTypesOfAttachment && (attachmentTypes?.length ?? 0) > 0,
      [showTypesOfAttachment, attachmentTypes?.length],
    );

    const showDescription = useMemo(() => typeof description === 'string', [description]);

    const noAttachments = useMemo(
      () => (isMulti ? attachments?.length === 0 : !selectedFile),
      [selectedFile, attachments?.length, isMulti],
    );

    const attachmentsForDisplay = useMemo(
      () =>
        showTypesOfAttachment
          ? attachments
          : attachments?.map(attachment => ({
              ...attachment,
              typeOfAttachment: null,
            })),
      [attachments, showTypesOfAttachment],
    );

    const newAttachmentsToRender = useMemo(() => newAttachments ?? [], [newAttachments]);

    const existingAttachmentsForDisplay = useMemo(() => attachmentsForDisplay ?? [], [attachmentsForDisplay]);

    const limitReached = useMemo(
      () => !!(amountLimit && attachmentsForDisplay?.length && attachmentsForDisplay.length >= amountLimit),
      [amountLimit, attachmentsForDisplay?.length],
    );

    const perFileHasNoAttachments = useMemo(
      () => newAttachmentsToRender.length === 0 && existingAttachmentsForDisplay.length === 0,
      [newAttachmentsToRender.length, existingAttachmentsForDisplay.length],
    );

    const processFiles = useCallback(
      (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) {
          return;
        }

        const incomingFiles = multiple ? Array.from(fileList) : [fileList[0]];

        const validFiles: File[] = [];
        let hasOversizedFile = false;
        let hasUnacceptedFormat = false;

        incomingFiles.forEach(file => {
          // `acceptFormats` is enforced natively only for the browse dialog, so
          // validate it here as well to cover drag-and-dropped files.
          if (!isFileAccepted(file, acceptFormats)) {
            hasUnacceptedFormat = true;

            return;
          }

          if (!!fileSizeValidation && file.size / 1024 >= fileSizeValidation.maxFileSizeKb) {
            hasOversizedFile = true;

            return;
          }

          validFiles.push(file);
        });

        if (hasUnacceptedFormat) {
          showAlert('Error! Unsupported file format.', { variant: 'error' });
        }

        if (hasOversizedFile && fileSizeValidation?.showAlert) {
          const maxFileSizeMb = fileSizeValidation.maxFileSizeKb / 1024;

          showAlert(
            fileSizeValidation.alert ??
              `Error! File is too large. There is a maximum limit of ${
                Number.isInteger(maxFileSizeMb) ? maxFileSizeMb : getFlooredFixed(maxFileSizeMb, 1)
              }MB.`,
            {
              variant: fileSizeValidation.alertType ?? 'error',
            },
          );
        }

        if (validFiles.length === 0) {
          return;
        }

        if (multiple && onAddFiles) {
          onAddFiles(validFiles);
        } else {
          changeSelectedFile?.(validFiles[0]);
        }
      },
      [multiple, acceptFormats, fileSizeValidation, onAddFiles, changeSelectedFile, showAlert],
    );

    const onChangeSelectedFile = (e: ChangeEvent<HTMLInputElement>) => {
      processFiles(e.target.files);

      if (inputFileRef.current) {
        inputFileRef.current.value = '';
      }
    };

    const onChangeDescription = (e: ChangeEvent<HTMLInputElement>) => {
      if (changeDescription) {
        changeDescription(e.target.value);
      }
    };

    const onDeleteSelectedFile = () => {
      changeSelectedFile?.(null);

      if (inputFileRef.current) {
        inputFileRef.current.value = '';
      }
    };

    const handleDeleteAttachment = useCallback(
      (id: number) => {
        if (onDeleteAttachment) {
          onDeleteAttachment(id);
        }
      },
      [onDeleteAttachment],
    );

    const handlePreviewAttachment = useCallback(
      (url: string, attachment?: Attachment) => {
        if (onPreviewAttachment) {
          onPreviewAttachment(url, attachment);
        }
      },
      [onPreviewAttachment],
    );

    const handleTypeChange = (event: ChangeEvent<{ name?: string; value: unknown }>) => {
      if (changeSelectedType) {
        const selectedTypeId = event.target.value as number;
        const type = attachmentTypes?.find(t => t.id === selectedTypeId) || null;

        changeSelectedType(type);
      }
    };

    // Create object URL for selectedFile preview and clean it up
    const selectedFileUrl = useMemo(() => {
      if (!selectedFile) {
        return undefined;
      }
      return URL.createObjectURL(selectedFile);
    }, [selectedFile]);

    // Cleanup object URL when component unmounts or file changes
    useEffect(() => {
      return () => {
        if (selectedFileUrl) {
          URL.revokeObjectURL(selectedFileUrl);
        }
      };
    }, [selectedFileUrl]);

    if (variant === 'perFile') {
      return (
        <div className={styles.root}>
          {hideBottomAttachmentsSection && (
            <AttachmentsSection
              attachments={[]}
              isEmpty={perFileHasNoAttachments}
              hideLabel={hideLabel}
              required={required && perFileHasNoAttachments}
              loading={loading}
              hasError={hasError}
            />
          )}

          {newAttachmentsToRender.length > 0 && (
            <div className={styles.attachmentsList}>
              {newAttachmentsToRender.map((attachment, index) => (
                <Fragment key={attachment.id}>
                  <NewAttachmentCard
                    attachment={attachment}
                    attachmentTypes={attachmentTypes}
                    showTypeSelect={showTypeSelect}
                    disabled={disabled}
                    onPreview={() => handlePreviewAttachment(attachment.url, attachment)}
                    onDelete={() => handleDeleteAttachment(attachment.id)}
                    onChangeType={type => onChangeAttachmentType?.(attachment.id, type)}
                    onChangeDescription={description => onChangeAttachmentDescription?.(attachment.id, description)}
                  />

                  {(index !== newAttachmentsToRender.length - 1 || !hideBottomAttachmentsSection) && <Divider />}
                </Fragment>
              ))}
            </div>
          )}

          {hideBottomAttachmentsSection && <Divider />}

          <DropZone enabled={enableDragAndDrop} disabled={disabled || limitReached} onFilesDropped={processFiles}>
            <BrowseFileButton
              inputFileRef={inputFileRef}
              acceptFormats={acceptFormats}
              multiple={multiple}
              disabled={{
                value: disabled || limitReached,
                reason: limitReached ? 'Attachments limit reached!' : undefined,
              }}
              onChange={onChangeSelectedFile}
            />
          </DropZone>

          {!hideBottomAttachmentsSection && (
            <>
              <Divider />

              <AttachmentsSection
                attachments={existingAttachmentsForDisplay}
                isEmpty={existingAttachmentsForDisplay.length === 0}
                hideLabel={hideLabel}
                required={required}
                loading={loading}
                disabled={disabled}
                hasError={hasError}
                onPreview={handlePreviewAttachment}
                onDelete={handleDeleteAttachment}
              />
            </>
          )}
        </div>
      );
    }

    return (
      <div className={styles.root}>
        {showTypeSelect && (
          <Select
            value={selectedType?.id || ''}
            label="Type of attachment"
            variant="outlined"
            disabled={limitReached}
            onChange={handleTypeChange}
          >
            {attachmentTypes?.map(type => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        )}

        {showDescription && (
          <TextArea
            variant="outlined"
            label="Description"
            size="medium"
            fullWidth
            value={description}
            onChange={onChangeDescription}
            disabled={limitReached}
          />
        )}

        {isMulti && (
          <>
            <DropZone enabled={enableDragAndDrop} disabled={disabled || limitReached} onFilesDropped={processFiles}>
              <BrowseFileButton
                inputFileRef={inputFileRef}
                acceptFormats={acceptFormats}
                multiple={multiple}
                disabled={{
                  value: limitReached || disabled,
                  reason: limitReached ? 'Attachments limit reached!' : undefined,
                }}
                onChange={onChangeSelectedFile}
              />
            </DropZone>

            <Divider />
          </>
        )}

        <AttachmentsSection
          attachments={isMulti ? (attachmentsForDisplay ?? []) : []}
          selectedFile={
            !isMulti && selectedFile
              ? { name: selectedFile.name, fileUrl: selectedFileUrl, onDelete: onDeleteSelectedFile }
              : null
          }
          isEmpty={noAttachments}
          hideLabel={hideLabel}
          required={required}
          loading={loading}
          disabled={disabled}
          hasError={hasError}
          onPreview={handlePreviewAttachment}
          onDelete={handleDeleteAttachment}
        />

        {!isMulti && !selectedFile && (
          <DropZone enabled={enableDragAndDrop} disabled={disabled} onFilesDropped={processFiles}>
            <BrowseFileButton
              inputFileRef={inputFileRef}
              acceptFormats={acceptFormats}
              multiple={multiple}
              disabled={{ value: disabled }}
              onChange={onChangeSelectedFile}
            />
          </DropZone>
        )}
      </div>
    );
  },
);
